import { DependenciesSection } from './DependenciesSection';
import { DetailsSection } from './DetailsSection';
import { useCallback, useMemo, memo } from 'react';
import { useSelector } from 'react-redux';
import {
  SWEEP_ELEMENTS,
  excludedFromDetails,
  isFieldType,
  isNonSweepItemMapEmpty,
} from './helpers';
import { mergeConfigurationMaps } from './annotations/tags/utils';
import { isWhatDoesItDoAllowed } from './configuration-chat/configurationChatConsts';
import { useDependencies } from './hooks/useDependencies';
import { selectOpenAiDescriptionForRule } from '../../../reducers/documentation/documentationReducer';
import { ConfigurationToName } from './types';
import { LatestDeployedSection } from './latest-deployed/LatestDeployedSection';
import { SweepElementTypes } from '../types';
import { useGetUsedBySweepElements } from './hooks/useGetUsedBySweepElements';
import { useGetParsedDependencies } from './hooks/useGetParsedDependencies';
import { useGetSweepElementsDependencies } from './hooks/useGetSweepElementsDependencies';
import { telemetry } from '../../../telemetry';
import { TagsSection } from './annotations/tags/TagsSection';
import { ServiceNowMigrationSection } from '../../pages/org-migration-demo/components/ServiceNowMigrationSection';
import { UserStorySection } from '../../pages/org-migration-demo/components/UserStorySection';
import { UserStory } from '../../pages/org-migration-demo/types';
import { useActiveInactive } from '../building-blocks/activity-state/useActiveInactive';
import { ActiveState } from '../building-blocks/activity-state/helpers';
import { useInitializeOpenAiDescriptionForRule } from './hooks/useInitializeOpenAiDescriptionForRule';
import { DependenciesHeader } from './header/DependenciesHeader';
import { getItemType } from './header/utils';
import { selectUserInfoData } from '../../../reducers/userInfoReducer';
import { SalesforceItem, isFieldConfigurationItem } from '../../parser/ConfigurationItemTypes';
import { setSfFieldName } from '../../pages/rollups/rollupHelpers';
import { selectSaltoObjectTypes } from '../../../reducers/global/parserReducer';
import { useGetObjectDependencies } from './hooks/useGetObjectDependencies';
import { useLocation } from 'react-router';
import { appRoutes } from '../../../constants/appRoutes';
import { FIELD, OBJECT } from '@server/parser';
import { DocumentationCardContentLayout } from '../building-blocks/documentation-card/DocumentationCardContentLayout';

interface DependenciesContentProps {
  configurationItem: SalesforceItem;
  type: string;
  crmOrgId: string;
  isReadOnly?: boolean;
}

type OrgMigrationConfigurationItem = SalesforceItem & {
  serviceNowMapping?: string[];
  migrationConsiderations?: string[];
  userStory?: UserStory;
};

const DependenciesContentComponent = ({
  configurationItem,
  type,
  crmOrgId,
  isReadOnly = false,
}: DependenciesContentProps) => {
  const location = useLocation();
  const isOrgMigrationRoute = location.pathname.startsWith(appRoutes.orgMigration.route);
  const orgMigrationItem = configurationItem as OrgMigrationConfigurationItem;
  const { id, name, objectApiNames, _oldId } = configurationItem ?? {};
  const dependencyType = 'fieldType' in configurationItem ? configurationItem.fieldType : '';
  const _objectName = objectApiNames?.[0];

  const openAiDescription = useSelector(
    selectOpenAiDescriptionForRule(configurationItem.metadataId ?? '', crmOrgId),
  );
  const isSweepElement = SWEEP_ELEMENTS.includes(type);

  const user = useSelector(selectUserInfoData);
  const disableAi = !!user?.account?.disableAi || isSweepElement;

  const {
    handleSetConfigurationItem,
    getConfigurationDependencies,
    getConfigurationUsedBy,
    getFieldConsumers,
    getOpenAiDescriptionForRule,
    onGoBackInHistory,
    historyLength,
  } = useDependencies(crmOrgId);

  const isNotFieldType = !!type && type !== FIELD;
  const isNotObjectType = !!type && type !== OBJECT;

  useInitializeOpenAiDescriptionForRule({
    metadataId: configurationItem.metadataId ?? '',
    crmOrgId,
    shouldDataBeCalled: isNotObjectType && isNotFieldType && !isSweepElement,
    getOpenAiDescriptionForRule,
  });

  //filters below can coexist but they shouldn't share state so 3 separate initializations are necessary
  //dependsOnActiveState, usedByActiveState, recordTypesActiveState,
  const {
    activeState: dependsOnActiveState,
    onToggleActiveInactive: _dependsOnToggleActiveInactive,
  } = useActiveInactive();

  const { activeState: usedByActiveState, onToggleActiveInactive: _usedByOnToggleActiveInactive } =
    useActiveInactive();

  const {
    activeState: configurationsActiveState,
    onToggleActiveInactive: _configurationsOnToggleActiveInactive,
  } = useActiveInactive();

  const _getConfigurationUsedBy = useCallback(async () => {
    if (type && !isSweepElement) {
      switch (type) {
        case FIELD:
          if (name && _objectName && configurationItem.metadataId) {
            await getFieldConsumers({
              metadataId: configurationItem.metadataId,
              fieldName: name,
              objectName: _objectName,
              crmOrgId,
            });
          } else {
            telemetry.captureError(new Error('missing values for getFieldConsumers'), {
              metadataId: configurationItem.metadataId,
            });
          }
          break;

        default:
          await getConfigurationUsedBy({
            crmOrgId,
            metadataId: configurationItem.metadataId,
          });
      }
    }
  }, [
    type,
    name,
    _objectName,
    getConfigurationUsedBy,
    getFieldConsumers,
    crmOrgId,
    isSweepElement,
    configurationItem.metadataId,
  ]);

  // custom fields ids are being provided from BE as sf field name + objectName, not as real id
  const configurationIdForSweepElements = isFieldConfigurationItem(configurationItem)
    ? setSfFieldName(configurationItem.name, _objectName)
    : id;

  const dependsOnForSweepElement = useGetSweepElementsDependencies(
    crmOrgId,
    configurationIdForSweepElements,
    type as SweepElementTypes,
    dependsOnActiveState === ActiveState.activeOnly,
  );

  const dependsOnItems = useGetParsedDependencies({
    crmOrgId,
    metadataId: configurationItem.metadataId,
    type: 'dependsOnIdsForRules',
    onlyActive: dependsOnActiveState === ActiveState.activeOnly,
  });

  const usedByItems = useGetParsedDependencies({
    crmOrgId,
    metadataId: configurationItem.metadataId,
    type: isFieldConfigurationItem(configurationItem) ? 'usedByIdsForFields' : 'usedByIdsForRules',
    onlyActive: usedByActiveState === ActiveState.activeOnly,
  });

  const {
    attributions: usedByObject = {},
    sweepElementsAttributions: usedObjectBySweepElements = {},
  } = useGetObjectDependencies({
    crmOrgId,
    objectName: configurationItem.name ?? '',
    isSupportedType: type === OBJECT,
    onlyActive: configurationsActiveState === ActiveState.activeOnly,
  });

  const usedBySweepElements = useGetUsedBySweepElements({
    crmOrgId,
    configurationId: configurationIdForSweepElements,
    onlyActive: usedByActiveState === ActiveState.activeOnly,
  });

  const configurationChatObjectName = _objectName;
  const saltoObjectTypes = useSelector(selectSaltoObjectTypes(crmOrgId));
  const showAiSection = !disableAi && type && isWhatDoesItDoAllowed(type);
  const isField = isFieldType(type);

  const itemType = useMemo(() => {
    return getItemType({
      saltoObjectTypes: saltoObjectTypes ?? [],
      _objectName,
      parentType: type,
      dependencyType,
    });
  }, [saltoObjectTypes, _objectName, type, dependencyType]);

  const mergedConfigurationItems = useMemo(
    () => mergeConfigurationMaps(usedByObject, usedObjectBySweepElements),
    [usedByObject, usedObjectBySweepElements],
  );

  const relatedListsItems = useMemo(() => {
    const relatedList = 'relatedLists' in configurationItem ? configurationItem.relatedLists : [];
    if (!relatedList?.length) {
      return undefined;
    }
    return { relatedList: relatedList ?? [] };
  }, [configurationItem]);

  const mergedUsedByItems = useMemo(
    () => mergeConfigurationMaps(usedByItems, usedBySweepElements),
    [usedByItems, usedBySweepElements],
  );

  const mergedDependsOnItems = useMemo(
    () =>
      isSweepElement
        ? mergeConfigurationMaps(dependsOnItems, dependsOnForSweepElement)
        : dependsOnItems,
    [isSweepElement, dependsOnForSweepElement, dependsOnItems],
  );

  const isObjectType = OBJECT === type;

  const shouldFetchDependencies = !isSweepElement && isNonSweepItemMapEmpty(dependsOnItems);
  const shouldFetchUsedBy = !isSweepElement && isNonSweepItemMapEmpty(usedByItems);

  return (
    <DocumentationCardContentLayout
      searchProviderProps={{
        configurationItem,
        dependsOnItems: mergedDependsOnItems,
        usedByItems: mergedUsedByItems,
        configurationItems: mergedConfigurationItems,
        relatedListsItems,
        saltoObjectTypes,
        dependsOnActiveState,
        usedByActiveState,
        configurationsActiveState,
      }}
      renderHeader={({ onOpenChat }) => (
        <DependenciesHeader
          configurationItem={configurationItem}
          type={type}
          crmOrgId={crmOrgId}
          shouldDisplayBackButton={historyLength > 1}
          isReadOnly={isReadOnly}
          isOrgMigrationRoute={isOrgMigrationRoute}
          showAskAiButton={!!type && !!name && !disableAi && !!configurationItem.metadataId}
          onAskAiClick={onOpenChat}
          onGoBack={onGoBackInHistory}
          saltoObjectTypes={saltoObjectTypes}
        />
      )}
      showAiSection={!!showAiSection}
      openAiDescription={openAiDescription}
      chatConfig={
        type && name
          ? {
              type,
              name: configurationItem.name,
              configurationItem,
              crmOrgId,
              itemType,
              configurationChatObjectName: configurationChatObjectName ?? '',
            }
          : undefined
      }
    >
      {type && !excludedFromDetails.includes(type) && (
        <DetailsSection
          key={`details-${id}`}
          configurationItem={configurationItem}
          isField={isField}
          isExpandedByDefault={!showAiSection}
        />
      )}

      {isObjectType && (
        <DependenciesSection
          metadataId={configurationItem.metadataId}
          crmOrgId={crmOrgId}
          key={`configurations-${id}.${name}`}
          configurationItems={mergedConfigurationItems}
          title="Configurations"
          onTableItemClick={handleSetConfigurationItem}
          onToggleActiveInactive={_configurationsOnToggleActiveInactive}
          activeState={configurationsActiveState}
        />
      )}

      {!isObjectType && (
        <DependenciesSection
          key={`dependsOn-${id}.${name}`}
          metadataId={configurationItem.metadataId}
          crmOrgId={crmOrgId}
          configurationItems={mergedDependsOnItems}
          title="Dependencies"
          shouldFetchOnMount={shouldFetchDependencies}
          fetchApiOnMount={getConfigurationDependencies}
          onTableItemClick={handleSetConfigurationItem}
          onToggleActiveInactive={_dependsOnToggleActiveInactive}
          activeState={dependsOnActiveState}
        />
      )}

      {!isObjectType && (
        <DependenciesSection
          metadataId={configurationItem.metadataId}
          crmOrgId={crmOrgId}
          key={`usedBy-${id}.${name}`}
          configurationItems={mergedUsedByItems}
          title="Used by"
          shouldFetchOnMount={shouldFetchUsedBy}
          fetchApiOnMount={_getConfigurationUsedBy}
          onTableItemClick={handleSetConfigurationItem}
          onToggleActiveInactive={_usedByOnToggleActiveInactive}
          activeState={usedByActiveState}
        />
      )}

      {isOrgMigrationRoute && orgMigrationItem.serviceNowMapping && (
        <ServiceNowMigrationSection
          title="ServiceNow Mapping"
          items={orgMigrationItem.serviceNowMapping}
          isExpandedByDefault
        />
      )}

      {isOrgMigrationRoute && orgMigrationItem.migrationConsiderations && (
        <ServiceNowMigrationSection
          title="Migration Considerations"
          items={orgMigrationItem.migrationConsiderations}
        />
      )}

      {isOrgMigrationRoute && orgMigrationItem.userStory && (
        <UserStorySection userStory={orgMigrationItem.userStory} />
      )}

      {isSweepElement && (
        <LatestDeployedSection
          sweepElementType={type as SweepElementTypes}
          parentElementId={configurationItem.id}
          crmOrgId={crmOrgId}
        />
      )}

      {/* TODO: temporary fix for missing internal id until BE is migrated to metadataId */}
      {!isReadOnly && !isOrgMigrationRoute && _oldId !== 'MissingInternalId' && type !== OBJECT && (
        <TagsSection
          crmOrgId={crmOrgId}
          configurationItemId={configurationItem.id}
          objectNames={_objectName ? [_objectName] : objectApiNames}
          configurationItemType={
            ConfigurationToName[configurationItem.parentType] ?? configurationItem.parentType
          }
        />
      )}
    </DocumentationCardContentLayout>
  );
};

export const DependenciesContent = memo(DependenciesContentComponent);
